import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'

import type { ReactElement } from 'react'
import type { SubmitHandler } from 'react-hook-form'

import Button from '~/components/Buttons/Button'
import TextArea from '~/components/Inputs/Basic/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import UserCardWithCall from '~/components/UserCardWithCall'
import { keys, useSetCommentsMutation } from '~/queries/ua-campaign'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  author: string | null
  date: string | null
  defaultValue: string
  id: string
  isOpen: boolean
  onClose: VoidFunction
}

type Inputs = {
  comment: string
}

const CommentModal = ({ author, date, defaultValue, id, isOpen, onClose }: Props): ReactElement => {
  /** Local state */
  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { isPending, mutateAsync: setCommentMutation } = useSetCommentsMutation()

  /** Handlers */

  const { handleSubmit, register } = useForm<Inputs>()

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (id) {
      try {
        await setCommentMutation({ comment: data.comment.trim(), id })
        await queryClient.invalidateQueries({ queryKey: keys.list() })
        onClose()
        displayToast('Your comment has successfully been sent', 'success')
      } catch (e) {
        displayToast('Something went wrong, please try again', 'error')
        captureException(e)
      }
    }
  }
  return (
    <Modal className="ua-management-comment-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader className="ua-management-comment-modal__header">
        <span>Comment</span>
        {author ? (
          <UserCardWithCall
            createdAt={dayjs(date).valueOf()}
            label="Last update by"
            labelPosition="left"
            userId={author}
          />
        ) : null}
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <TextArea {...register('comment')} defaultValue={defaultValue} placeholder="Please write your comment" />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isPending} type="submit">
            Submit
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default CommentModal
